import React, { createContext, useReducer, useContext, useEffect, useRef } from 'react';
import { clockOutSession, getSessionTimeoutDelay } from '../utils/sessionTimeoutUtils';
import logger from '../utils/logger';
import { cacheSessionFeatureAccess, clearAllLockedFeatures, clearTenantLockedFeatures } from '../utils/featureAccessUtils';


const IMPERSONATION_KEYS = {
  isImpersonating: 'isImpersonating',
  tenantId: 'impersonationTenantId',
  tenantName: 'impersonationTenantName',
  startedAt: 'impersonationStartedAt',
  originalSession: 'originalAuthSession',
};

const parseJson = (value, fallback = null) => {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const unauthenticatedState = {
  isAuthenticated: false,
  isInitializing: false,
  user: null,
  token: null,
  activeTenantId: null,
  availableTenants: [],
  isImpersonating: false,
  impersonationTenantId: null,
  impersonationTenantName: null,
  impersonationStartedAt: null,
  originalAuthSession: null,
};

const getStoredAuthState = () => {
  if (typeof sessionStorage === 'undefined') {
    return { ...unauthenticatedState, isInitializing: true };
  }

  const token = sessionStorage.getItem('appToken');
  if (!token) {
    return unauthenticatedState;
  }

  const availableTenants = parseJson(sessionStorage.getItem('availableTenants'), []);
  const isImpersonating = sessionStorage.getItem(IMPERSONATION_KEYS.isImpersonating) === 'true';

  return {
    ...unauthenticatedState,
    isAuthenticated: true,
    token,
    user: {
      email: sessionStorage.getItem('userEmail'),
      role: sessionStorage.getItem('userRole'),
      id: sessionStorage.getItem('employeeId'),
    },
    activeTenantId: sessionStorage.getItem('activeTenantId'),
    availableTenants,
    isImpersonating,
    impersonationTenantId: isImpersonating ? sessionStorage.getItem(IMPERSONATION_KEYS.tenantId) : null,
    impersonationTenantName: isImpersonating ? sessionStorage.getItem(IMPERSONATION_KEYS.tenantName) : null,
    impersonationStartedAt: isImpersonating ? sessionStorage.getItem(IMPERSONATION_KEYS.startedAt) : null,
    originalAuthSession: isImpersonating
      ? parseJson(sessionStorage.getItem(IMPERSONATION_KEYS.originalSession), null)
      : null,
  };
};

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        isInitializing: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        activeTenantId: action.payload.activeTenantId || null,
        availableTenants: action.payload.availableTenants || [],
        isImpersonating: false,
        impersonationTenantId: null,
        impersonationTenantName: null,
        impersonationStartedAt: null,
        originalAuthSession: null,
      };
    case 'LOGOUT':
      return {
        ...unauthenticatedState,
        isInitializing: false,
      };
    case 'RESTORE':
      return {
        ...state,
        isInitializing: false,
        isAuthenticated: !!action.payload.token,
        user: action.payload.user,
        token: action.payload.token,
        activeTenantId: action.payload.activeTenantId,
        availableTenants: action.payload.availableTenants || [],
        isImpersonating: action.payload.isImpersonating || false,
        impersonationTenantId: action.payload.impersonationTenantId || null,
        impersonationTenantName: action.payload.impersonationTenantName || null,
        impersonationStartedAt: action.payload.impersonationStartedAt || null,
        originalAuthSession: action.payload.originalAuthSession || null,
      };
    case 'SET_ACTIVE_TENANT':
      return {
        ...state,
        activeTenantId: action.payload,
      };
    case 'SYNC_AUTH_SESSION':
      return {
        ...state,
        isAuthenticated: !!action.payload.token,
        user: action.payload.user || state.user,
        token: action.payload.token,
        activeTenantId: action.payload.activeTenantId || null,
        availableTenants: action.payload.availableTenants || state.availableTenants || [],
      };
    case 'START_IMPERSONATION':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        activeTenantId: action.payload.activeTenantId,
        availableTenants: action.payload.availableTenants || [],
        isImpersonating: true,
        impersonationTenantId: action.payload.impersonationTenantId,
        impersonationTenantName: action.payload.impersonationTenantName,
        impersonationStartedAt: action.payload.impersonationStartedAt,
        originalAuthSession: action.payload.originalAuthSession,
      };
    case 'STOP_IMPERSONATION':
      return {
        ...state,
        isAuthenticated: !!action.payload.token,
        user: action.payload.user,
        token: action.payload.token,
        activeTenantId: action.payload.activeTenantId,
        availableTenants: action.payload.availableTenants || [],
        isImpersonating: false,
        impersonationTenantId: null,
        impersonationTenantName: null,
        impersonationStartedAt: null,
        originalAuthSession: null,
      };
    default:
      return state;
  }
}

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, undefined, getStoredAuthState);
  const sessionTimeoutRef = useRef(null);

  // Restore auth state from sessionStorage on mount
  useEffect(() => {
    const token = sessionStorage.getItem('appToken');
    const userEmail = sessionStorage.getItem('userEmail');
    const userRole = sessionStorage.getItem('userRole');
    const employeeId = sessionStorage.getItem('employeeId');
    const activeTenantId = sessionStorage.getItem('activeTenantId');
    const availableTenantsJson = sessionStorage.getItem('availableTenants');
    const availableTenants = parseJson(availableTenantsJson, []);
    const isImpersonating = sessionStorage.getItem(IMPERSONATION_KEYS.isImpersonating) === 'true';
    const originalAuthSession = parseJson(
      sessionStorage.getItem(IMPERSONATION_KEYS.originalSession),
      null,
    );

    // Always dispatch RESTORE to complete initialization, even if no token exists
    dispatch({
      type: 'RESTORE',
      payload: {
        token: token || null,
        user: token ? { email: userEmail, role: userRole, id: employeeId } : null,
        activeTenantId: token ? activeTenantId : null,
        availableTenants: token ? availableTenants : [],
        isImpersonating: token ? isImpersonating : false,
        impersonationTenantId: token ? sessionStorage.getItem(IMPERSONATION_KEYS.tenantId) : null,
        impersonationTenantName: token ? sessionStorage.getItem(IMPERSONATION_KEYS.tenantName) : null,
        impersonationStartedAt: token ? sessionStorage.getItem(IMPERSONATION_KEYS.startedAt) : null,
        originalAuthSession: token ? originalAuthSession : null,
      },
    });
  }, []);

  // Login action
  const login = (token, user, activeTenantId = null, availableTenants = [], features) => {
    clearAllLockedFeatures();
    cacheSessionFeatureAccess(features, activeTenantId);
    sessionStorage.setItem('appToken', token);
    sessionStorage.setItem('userEmail', user.email);
    sessionStorage.setItem('userRole', user.role);
    sessionStorage.setItem('employeeId', user.id);
    sessionStorage.setItem('activeTenantId', activeTenantId || '');
    sessionStorage.setItem('availableTenants', JSON.stringify(availableTenants));
    sessionStorage.removeItem(IMPERSONATION_KEYS.isImpersonating);
    sessionStorage.removeItem(IMPERSONATION_KEYS.tenantId);
    sessionStorage.removeItem(IMPERSONATION_KEYS.tenantName);
    sessionStorage.removeItem(IMPERSONATION_KEYS.startedAt);
    sessionStorage.removeItem(IMPERSONATION_KEYS.originalSession);
    dispatch({
      type: 'LOGIN',
      payload: { token, user, activeTenantId, availableTenants },
    });
  };

  useEffect(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }

    if (!state.isAuthenticated || !state.token) {
      return undefined;
    }

    const timeoutDelay = getSessionTimeoutDelay(state.token);
    if (timeoutDelay === null) {
      return undefined;
    }

    const handleSessionTimeout = async () => {
      try {
        await clockOutSession({ token: state.token });
      } catch (error) {
        logger.warn('Automatic clock-out request failed. Clearing the local session anyway.', error);
      } finally {
        sessionStorage.clear();
        dispatch({ type: 'LOGOUT' });
        window.location.replace('/');
      }
    };

    sessionTimeoutRef.current = window.setTimeout(() => {
      void handleSessionTimeout();
    }, timeoutDelay);

    return () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }
    };
  }, [state.isAuthenticated, state.token, state.user?.id]);

  // Logout action
  const logout = () => {
    sessionStorage.clear();
    dispatch({ type: 'LOGOUT' });
  };

  // Set active tenant
  const setActiveTenant = (tenantId) => {
    sessionStorage.setItem('activeTenantId', tenantId);
    dispatch({ type: 'SET_ACTIVE_TENANT', payload: tenantId });
  };

  const syncAuthSession = ({ token, user, activeTenantId, availableTenants, features }) => {
    const nextTenantId = activeTenantId !== undefined ? activeTenantId : state.activeTenantId;
    if (String(nextTenantId) !== String(state.activeTenantId) || (token && token !== state.token)) {
      clearTenantLockedFeatures(nextTenantId);
    }
    cacheSessionFeatureAccess(features, nextTenantId);
    if (token) {
      sessionStorage.setItem('appToken', token);
    }
    if (user?.email !== undefined) {
      sessionStorage.setItem('userEmail', user.email || '');
    }
    if (user?.role !== undefined) {
      sessionStorage.setItem('userRole', user.role || '');
    }
    if (user?.id !== undefined) {
      sessionStorage.setItem('employeeId', user.id || '');
    }
    if (activeTenantId !== undefined) {
      sessionStorage.setItem('activeTenantId', activeTenantId || '');
    }
    if (Array.isArray(availableTenants)) {
      sessionStorage.setItem('availableTenants', JSON.stringify(availableTenants));
    }

    dispatch({
      type: 'SYNC_AUTH_SESSION',
      payload: {
        token: token || state.token,
        user: user || state.user,
        activeTenantId: activeTenantId !== undefined ? activeTenantId : state.activeTenantId,
        availableTenants: Array.isArray(availableTenants)
          ? availableTenants
          : state.availableTenants,
      },
    });
  };

  const startImpersonation = (impersonationData) => {
    const originalSession = state.originalAuthSession || {
      token: state.token || sessionStorage.getItem('appToken') || null,
      user: state.user || {
        email: sessionStorage.getItem('userEmail') || '',
        role: sessionStorage.getItem('userRole') || '',
        id: sessionStorage.getItem('employeeId') || '',
      },
      activeTenantId: state.activeTenantId || sessionStorage.getItem('activeTenantId') || null,
      availableTenants:
        state.availableTenants || parseJson(sessionStorage.getItem('availableTenants'), []),
    };

    const startedAt = new Date().toISOString();
    const impersonationTenantId = String(
      impersonationData?.activeTenantId || impersonationData?.tenantId || '',
    );

    clearTenantLockedFeatures(impersonationTenantId);
    cacheSessionFeatureAccess(impersonationData?.features, impersonationTenantId);

    sessionStorage.setItem('appToken', impersonationData.token);
    sessionStorage.setItem('userEmail', impersonationData?.user?.email || '');
    sessionStorage.setItem('userRole', impersonationData?.user?.role || 'Admin');
    sessionStorage.setItem('employeeId', impersonationData?.user?.id || '');
    sessionStorage.setItem('activeTenantId', impersonationTenantId);
    sessionStorage.setItem(
      'availableTenants',
      JSON.stringify(impersonationData?.availableTenants || []),
    );
    sessionStorage.setItem(IMPERSONATION_KEYS.isImpersonating, 'true');
    sessionStorage.setItem(IMPERSONATION_KEYS.tenantId, impersonationTenantId);
    sessionStorage.setItem(
      IMPERSONATION_KEYS.tenantName,
      impersonationData?.tenantName || '',
    );
    sessionStorage.setItem(IMPERSONATION_KEYS.startedAt, startedAt);
    sessionStorage.setItem(
      IMPERSONATION_KEYS.originalSession,
      JSON.stringify(originalSession),
    );

    dispatch({
      type: 'START_IMPERSONATION',
      payload: {
        token: impersonationData.token,
        user: impersonationData.user,
        activeTenantId: impersonationTenantId,
        availableTenants: impersonationData?.availableTenants || [],
        isImpersonating: true,
        impersonationTenantId,
        impersonationTenantName: impersonationData?.tenantName || '',
        impersonationStartedAt: startedAt,
        originalAuthSession: originalSession,
      },
    });
  };

  const stopImpersonation = () => {
    const originalSession =
      state.originalAuthSession ||
      parseJson(sessionStorage.getItem(IMPERSONATION_KEYS.originalSession), null);

    sessionStorage.removeItem(IMPERSONATION_KEYS.isImpersonating);
    sessionStorage.removeItem(IMPERSONATION_KEYS.tenantId);
    sessionStorage.removeItem(IMPERSONATION_KEYS.tenantName);
    sessionStorage.removeItem(IMPERSONATION_KEYS.startedAt);
    sessionStorage.removeItem(IMPERSONATION_KEYS.originalSession);

    if (!originalSession?.token || !originalSession?.user) {
      logout();
      return;
    }

    sessionStorage.setItem('appToken', originalSession.token);
    sessionStorage.setItem('userEmail', originalSession.user.email || '');
    sessionStorage.setItem('userRole', originalSession.user.role || 'SuperAdmin');
    sessionStorage.setItem('employeeId', originalSession.user.id || '');
    sessionStorage.setItem('activeTenantId', originalSession.activeTenantId || '');
    sessionStorage.setItem(
      'availableTenants',
      JSON.stringify(originalSession.availableTenants || []),
    );

    dispatch({
      type: 'STOP_IMPERSONATION',
      payload: {
        token: originalSession.token,
        user: originalSession.user,
        activeTenantId: originalSession.activeTenantId || null,
        availableTenants: originalSession.availableTenants || [],
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        setActiveTenant,
        syncAuthSession,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
